import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, PaperPlaneRight, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail, trackPage } from "../lib/api";
import { subscribeContentUpdates } from "../lib/contentSync";
import { useLang } from "../i18n";

const MEDAL_LABEL = { gold: "Emas", silver: "Perak", bronze: "Perunggu" };
const MEDAL_CLS = {
    gold: "text-[#B8890B] border-[#FFB800]",
    silver: "text-racing-muted border-racing-muted",
    bronze: "text-[#B0763F] border-[#B0763F]",
};

export default function AtletDetail() {
    const { id } = useParams();
    const { t } = useLang();
    const [athlete, setAthlete] = useState(null);
    const [likeCount, setLikeCount] = useState(0);
    const [liked, setLiked] = useState(false);
    const [comments, setComments] = useState([]);
    const [supportForm, setSupportForm] = useState({ name: "", message: "" });
    const [sending, setSending] = useState(false);
    const [fundForm, setFundForm] = useState({ name: "", amount: "", hide_name: true, message: "", email: "" });
    const [fundSending, setFundSending] = useState(false);
    const [fundTotal, setFundTotal] = useState(0);
    const [fundSupporters, setFundSupporters] = useState([]);
    const [replyOpen, setReplyOpen] = useState("");
    const [replyForm, setReplyForm] = useState({ name: "", message: "" });
    const [replySending, setReplySending] = useState(false);
    const loadAthleteRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        trackPage(`/atlet/${id}`);
        const params = new URLSearchParams(window.location.search);
        const fundOrderId = params.get("order_id");
        if (params.get("dukungan") === "finish" && fundOrderId) {
            window.history.replaceState(null, "", window.location.pathname);
            const checkFundStatus = async (attempt = 0) => {
                try {
                    const { data: st } = await api.get(`/athletes/funds/${fundOrderId}/status`);
                    if (st.status === "paid") {
                        toast.success("Dukungan uang latihan diterima! Terima kasih banyak.");
                        loadAthleteRef.current?.();
                    } else if (st.status === "failed") {
                        toast.error("Pembayaran tidak berhasil atau kedaluwarsa. Silakan coba lagi.");
                    } else if (attempt < 6) {
                        setTimeout(() => checkFundStatus(attempt + 1), 3000);
                    } else {
                        toast.info("Pembayaran sedang diproses. Status akan diperbarui otomatis.");
                    }
                } catch (e) {
                    if (attempt < 6) setTimeout(() => checkFundStatus(attempt + 1), 3000);
                }
            };
            checkFundStatus();
        }
        const loadAthlete = () => {
            api.get(`/athletes/${id}`)
                .then((r) => {
                    setAthlete(r.data);
                    setLikeCount(r.data.like_count || 0);
                    setLiked(!!r.data.liked);
                    setComments(r.data.comments || []);
                    setFundTotal(r.data.fund_total || 0);
                    setFundSupporters(r.data.fund_supporters || []);
                })
                .catch(() => setAthlete(false));
        };
        loadAthleteRef.current = loadAthlete;
        loadAthlete();
        return subscribeContentUpdates(loadAthlete);
    }, [id]);

    const sendFund = async () => {
        const amount = Number(fundForm.amount || 0);
        if (!fundForm.name.trim() || fundForm.name.trim().length < 2) {
            toast.error("Isi nama kamu dulu (min. 2 huruf)");
            return;
        }
        if (amount < 10000) {
            toast.error("Dukungan uang latihan atlet minimal Rp 10.000");
            return;
        }
        setFundSending(true);
        try {
            const { data } = await api.post(`/athletes/${id}/funds`, { name: fundForm.name.trim(), amount, hide_name: fundForm.hide_name, message: fundForm.message.trim(), email: fundForm.email.trim() });
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
                return;
            }
            toast.error("Gagal membuka pembayaran online");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setFundSending(false);
        }
    };

    const sendReply = async (fundId) => {
        if (replyForm.name.trim().length < 2 || replyForm.message.trim().length < 2) {
            toast.error("Isi nama dan balasan dulu (min. 2 huruf)");
            return;
        }
        setReplySending(true);
        try {
            const { data } = await api.post(`/athletes/funds/${fundId}/replies`, { name: replyForm.name.trim(), message: replyForm.message.trim() });
            setFundSupporters((prev) => prev.map((f) => (f.id === fundId ? { ...f, replies: [...(f.replies || []), data] } : f)));
            setReplyForm({ name: "", message: "" });
            setReplyOpen("");
            toast.success("Balasan terkirim!");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setReplySending(false);
        }
    };

    const toggleLike = async () => {
        try {
            const { data } = await api.post(`/athletes/${id}/like`);
            setLiked(data.liked);
            setLikeCount(data.like_count);
        } catch (err) {
            toast.error("Gagal mengirim dukungan, coba lagi");
        }
    };

    const sendComment = async () => {
        if (!supportForm.name.trim() || !supportForm.message.trim()) {
            toast.error("Isi nama dan kata-kata dukungan dulu");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/athletes/${id}/comments`, supportForm);
            setComments((prev) => [data, ...prev]);
            setSupportForm({ name: "", message: "" });
            toast.success("Dukungan terkirim. Terima kasih!");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSending(false);
        }
    };

    if (athlete === null) {
        return <div className="min-h-screen bg-white" data-testid="athlete-loading" />;
    }

    if (athlete === false) {
        return (
            <div className="min-h-screen bg-white text-neutral-900 flex flex-col items-center justify-center gap-6" data-testid="athlete-not-found">
                <p className="font-display uppercase text-3xl">{t.athlete_notfound}</p>
                <Link to="/" className="text-xs uppercase tracking-[0.25em] text-racing-red" data-testid="athlete-back-link">
                    {t.back_home}
                </Link>
            </div>
        );
    }

    const medals = athlete.medals || [];
    const counts = { gold: 0, silver: 0, bronze: 0 };
    medals.forEach((m) => {
        if (counts[m.medal] !== undefined) counts[m.medal] += 1;
    });

    return (
        <div className="min-h-screen bg-white text-neutral-900" data-testid="athlete-page">
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
                    <Link
                        to="/"
                        data-testid="athlete-back-link"
                        className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-racing-muted hover:text-neutral-900 transition-colors duration-300"
                    >
                        <ArrowLeft size={16} weight="bold" /> {t.back}
                    </Link>

                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
                        <div className="lg:col-span-2">
                            <div className="relative border border-racing-line bg-racing-surface overflow-hidden">
                                {athlete.photo_url ? (
                                    <img
                                        src={fileUrl(athlete.photo_url)}
                                        alt={athlete.name}
                                        className="w-full h-[380px] md:h-[460px] object-cover"
                                        style={{ clipPath: "polygon(0 0, 100% 0, 100% 94%, 92% 100%, 0 100%)" }}
                                        data-testid="athlete-photo"
                                    />
                                ) : (
                                    <div className="w-full h-[380px] md:h-[460px] flex items-center justify-center" data-testid="athlete-photo">
                                        <UserCircle size={120} weight="duotone" className="text-neutral-900/10" />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="lg:col-span-3">
                            <p className="text-xs uppercase tracking-[0.3em] text-racing-red">{t.athlete_profile}</p>
                            <h1 className="mt-3 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="athlete-name">
                                {athlete.name}
                                {athlete.ku && (
                                    <span className="ml-3 inline-block align-middle bg-racing-gold text-neutral-900 text-xs sm:text-sm uppercase tracking-[0.2em] px-3 py-1.5" data-testid="athlete-ku-badge">
                                        {athlete.ku}
                                    </span>
                                )}
                            </h1>

                            <div className="mt-10 grid grid-cols-2 gap-px bg-racing-line border border-racing-line" data-testid="athlete-stats">
                                <div className="bg-racing-surface p-5 md:p-7">
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted">{t.athlete_birth}</p>
                                    <p className="mt-2 font-display text-2xl md:text-3xl text-neutral-900" data-testid="athlete-birth-year">{athlete.birth_year}</p>
                                </div>
                                <div className="bg-racing-surface p-5 md:p-7">
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted">{t.athlete_joined}</p>
                                    <p className="mt-2 font-display text-2xl md:text-3xl text-neutral-900" data-testid="athlete-joined-year">{athlete.joined_year}</p>
                                </div>
                            </div>

                            <div className="mt-8 flex flex-wrap gap-3" data-testid="athlete-medal-counts">
                                {Object.entries(counts).map(([k, v]) => (
                                    <span key={k} className={`text-[10px] uppercase tracking-[0.25em] border px-4 py-2 ${MEDAL_CLS[k]}`} data-testid={`medal-count-${k}`}>
                                        {v} {MEDAL_LABEL[k]}
                                    </span>
                                ))}
                            </div>

                            <div className="mt-10 border border-racing-line bg-racing-surface p-6 md:p-8" data-testid="athlete-support">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <h2 className="font-display uppercase text-xl md:text-2xl text-neutral-900">Dukungan untuk {athlete.name.split(" ")[0]}</h2>
                                    <button
                                        type="button"
                                        onClick={toggleLike}
                                        data-testid="athlete-like-button"
                                        className={`inline-flex items-center gap-2 border px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                                            liked
                                                ? "bg-racing-red border-racing-red text-white"
                                                : "border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red"
                                        }`}
                                    >
                                        <Heart size={18} weight={liked ? "fill" : "bold"} />
                                        <span data-testid="athlete-like-count">{likeCount}</span> Love
                                    </button>
                                </div>

                                <div className="mt-6" data-testid="fund-support">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <p className="text-[11px] uppercase tracking-[0.2em] text-racing-golddeep">Dukungan uang latihan atlet minimal Rp 10.000</p>
                                        <p className="text-sm text-neutral-900" data-testid="fund-total">Terkumpul: <span className="font-display text-racing-red">Rp {fundTotal.toLocaleString("id-ID")}</span></p>
                                    </div>
                                    {likeCount >= 100 ? (
                                        <>
                                    <div className="mt-4 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
                                        <input
                                            value={fundForm.name}
                                            onChange={(e) => setFundForm({ ...fundForm, name: e.target.value })}
                                            maxLength={40}
                                            placeholder="Nama kamu (ditampilkan tersensor)"
                                            data-testid="fund-name-input"
                                            className="bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        />
                                        <input
                                            type="number"
                                            min="10000"
                                            step="1000"
                                            value={fundForm.amount}
                                            onChange={(e) => setFundForm({ ...fundForm, amount: e.target.value })}
                                            placeholder="Nominal, min. 10000"
                                            data-testid="fund-amount-input"
                                            className="bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        />
                                        <button
                                            type="button"
                                            onClick={sendFund}
                                            disabled={fundSending}
                                            data-testid="fund-submit-button"
                                            className="inline-flex items-center justify-center gap-2 bg-racing-gold hover:bg-racing-golddeep disabled:opacity-60 text-neutral-900 text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors"
                                        >
                                            {fundSending ? "Memproses..." : "Kirim Dukungan"}
                                        </button>
                                    </div>
                                    <textarea
                                        value={fundForm.message}
                                        onChange={(e) => setFundForm({ ...fundForm, message: e.target.value })}
                                        maxLength={300}
                                        rows={2}
                                        placeholder="Tulis pesan dukungan untuk atlet (opsional, maks. 300 karakter)..."
                                        data-testid="fund-message-input"
                                        className="mt-3 w-full bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors resize-none"
                                    />
                                    <input
                                        type="email"
                                        value={fundForm.email}
                                        onChange={(e) => setFundForm({ ...fundForm, email: e.target.value })}
                                        maxLength={100}
                                        placeholder="Email kamu (opsional — bukti & pengingat pembayaran dikirim ke sini, BUKAN ke email klub)"
                                        data-testid="fund-email-input"
                                        className="mt-3 w-full bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    />
                                    <label className="mt-3 flex items-center gap-2 text-xs text-racing-muted cursor-pointer" data-testid="fund-hide-name-label">
                                        <input
                                            type="checkbox"
                                            checked={fundForm.hide_name}
                                            onChange={(e) => setFundForm({ ...fundForm, hide_name: e.target.checked })}
                                            data-testid="fund-hide-name-checkbox"
                                            className="accent-[#E02424]"
                                        />
                                        Sembunyikan nama saya di daftar pendukung publik (ditampilkan tersensor)
                                    </label>
                                        </>
                                    ) : (
                                        <p className="mt-4 border border-dashed border-racing-line bg-white px-4 py-3 text-sm text-racing-muted" data-testid="fund-locked-note">
                                            Terkunci — menu dukungan uang latihan terbuka setelah atlet ini mencapai 100 love. Saat ini baru {likeCount} love, yuk bantu dengan menekan tombol Love di atas!
                                        </p>
                                    )}
                                    <p className="mt-3 text-xs text-racing-muted" data-testid="fund-negotiate-note">
                                        Anda bisa WA ke{" "}
                                        <a href="https://wa.me/6285230009454" target="_blank" rel="noopener noreferrer" className="text-racing-red hover:text-racing-redhover font-medium">
                                            085230009454
                                        </a>{" "}
                                        jika ingin bernegosiasi untuk mensupport atlet ini secara pribadi/korporat dan membutuhkan feedback tertentu
                                    </p>
                                    {fundSupporters.length > 0 && (
                                        <div className="mt-4 space-y-3" data-testid="fund-supporter-list">
                                            {fundSupporters.map((f, i) => (
                                                <div key={f.id || i} className="border border-racing-line bg-white px-4 py-3" data-testid={`fund-supporter-${i}`}>
                                                    <p className="text-xs text-racing-muted">
                                                        <span className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{f.name}</span>
                                                        {" · "}<span className="text-neutral-900 font-medium">Rp {f.amount.toLocaleString("id-ID")}</span>
                                                    </p>
                                                    {f.message && <p className="mt-1 text-sm text-neutral-900/90" data-testid={`fund-supporter-message-${i}`}>&ldquo;{f.message}&rdquo;</p>}
                                                    {(f.replies || []).map((r, j) => (
                                                        <div key={r.id || j} className="mt-2 ml-4 border-l-2 border-racing-line pl-3" data-testid={`fund-reply-${i}-${j}`}>
                                                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">{r.name}</p>
                                                            <p className="mt-0.5 text-sm text-neutral-900/80">{r.message}</p>
                                                        </div>
                                                    ))}
                                                    {replyOpen === f.id ? (
                                                        <div className="mt-3 space-y-2" data-testid={`fund-reply-form-${i}`}>
                                                            <input
                                                                value={replyForm.name}
                                                                onChange={(e) => setReplyForm({ ...replyForm, name: e.target.value })}
                                                                maxLength={40}
                                                                placeholder="Nama kamu"
                                                                data-testid={`fund-reply-name-${i}`}
                                                                className="w-full bg-racing-surface border border-racing-line px-3 py-2 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                                            />
                                                            <div className="flex gap-2">
                                                                <input
                                                                    value={replyForm.message}
                                                                    onChange={(e) => setReplyForm({ ...replyForm, message: e.target.value })}
                                                                    maxLength={300}
                                                                    placeholder="Tulis balasan..."
                                                                    data-testid={`fund-reply-message-${i}`}
                                                                    className="flex-1 bg-racing-surface border border-racing-line px-3 py-2 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                                                    onKeyDown={(e) => { if (e.key === "Enter") sendReply(f.id); }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => sendReply(f.id)}
                                                                    disabled={replySending}
                                                                    data-testid={`fund-reply-submit-${i}`}
                                                                    className="inline-flex items-center gap-1.5 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-colors"
                                                                >
                                                                    <PaperPlaneRight size={13} weight="bold" /> {replySending ? "..." : "Kirim"}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => { setReplyOpen(f.id); setReplyForm({ name: "", message: "" }); }}
                                                            data-testid={`fund-reply-toggle-${i}`}
                                                            className="mt-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors"
                                                        >
                                                            Balas pesan ini
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-8 border-t border-racing-line pt-6 grid sm:grid-cols-[1fr_2fr_auto] gap-3">
                                    <p className="sm:col-span-3 text-[11px] uppercase tracking-[0.2em] text-racing-golddeep">Kata-kata dukungan</p>
                                    <input
                                        value={supportForm.name}
                                        onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                                        maxLength={40}
                                        placeholder="Nama kamu"
                                        data-testid="support-name-input"
                                        className="bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                    />
                                    <input
                                        value={supportForm.message}
                                        onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                                        maxLength={300}
                                        placeholder="Tulis kata-kata dukungan..."
                                        data-testid="support-message-input"
                                        className="bg-white border border-racing-line px-4 py-3 text-sm focus:outline-none focus:border-racing-red transition-colors"
                                        onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
                                    />
                                    <button
                                        type="button"
                                        onClick={sendComment}
                                        disabled={sending}
                                        data-testid="support-submit-button"
                                        className="inline-flex items-center justify-center gap-2 bg-racing-red hover:bg-racing-redhover disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors"
                                    >
                                        <PaperPlaneRight size={15} weight="bold" /> {sending ? "..." : "Kirim"}
                                    </button>
                                </div>

                                <div className="mt-6 space-y-3" data-testid="support-comment-list">
                                    {comments.length === 0 && (
                                        <p className="text-sm text-racing-muted" data-testid="support-comment-empty">Belum ada dukungan. Jadilah yang pertama menyemangati!</p>
                                    )}
                                    {comments.map((c, i) => (
                                        <div key={c.id} className="border border-racing-line bg-white px-4 py-3" data-testid={`support-comment-${i}`}>
                                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{c.name}</p>
                                            <p className="mt-1 text-sm text-neutral-900/90">{c.message}</p>
                                        </div>
                                    ))}
                                </div>

                            </div>

                            <h2 className="mt-12 font-display uppercase text-2xl md:text-3xl text-neutral-900">{t.athlete_history}</h2>
                            <div className="mt-6 border-t border-racing-line" data-testid="athlete-medal-history">
                                {medals.length === 0 ? (
                                    <p className="py-6 text-sm text-racing-muted" data-testid="athlete-medal-empty">
                                        {t.athlete_history_empty}
                                    </p>
                                ) : (
                                    medals.map((m, i) => (
                                        <div key={m.id} className="flex items-start justify-between gap-6 py-5 border-b border-racing-line" data-testid={`medal-row-${i}`}>
                                            <div>
                                                <span className={`inline-block text-[10px] uppercase tracking-[0.25em] border px-3 py-1 ${MEDAL_CLS[m.medal] || MEDAL_CLS.gold}`}>
                                                    {MEDAL_LABEL[m.medal] || m.medal}
                                                </span>
                                                <p className="mt-3 text-base md:text-lg text-neutral-900/90">{m.title}</p>
                                                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-racing-muted">{m.event}</p>
                                            </div>
                                            <span className="font-display text-2xl md:text-3xl text-neutral-900/20 shrink-0">{m.year}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
